<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-container" @keydown.esc="$emit('close')">
          <!-- Header -->
          <div class="modal-header">
            <div class="modal-icon">{{ persona.icon }}</div>
            <div class="modal-header-text">
              <h2 class="modal-title">{{ persona.name }}</h2>
              <div class="modal-meta">
                <span class="modal-category">{{ persona.category }}</span>
                <span class="modal-type-persona">🎭 Persona</span>
                <span v-if="persona.author" class="modal-author">👤 {{ persona.author }}</span>
                <span class="modal-stat">👁️ {{ persona.views || 0 }}</span>
                <span class="modal-stat">📥 {{ localDownloads }}</span>
              </div>
            </div>
            <button class="modal-close" @click="$emit('close')" title="Close">✕</button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <p class="modal-desc">{{ persona.description }}</p>

            <!-- Prompt -->
            <div class="modal-section">
              <h3 class="section-label">🎭 System Prompt</h3>
              <div class="persona-prompt-block">
                <pre class="persona-prompt-text">{{ persona.prompt }}</pre>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button
              class="btn-import"
              :class="{ disabled: !isLoggedIn }"
              :disabled="!isLoggedIn"
              @click="handleImport"
            >
              {{ isLoggedIn ? '📥 Import Persona' : '🔒 Login to Import' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
const API_BASE = import.meta.env.VITE_API_BASE || ''

export default {
  name: 'PersonaModal',
  props: {
    visible: { type: Boolean, default: false },
    persona: { type: Object, default: () => ({}) },
    isLoggedIn: { type: Boolean, default: false },
  },
  emits: ['close', 'downloaded'],
  data() {
    return {
      localDownloads: (this.persona && this.persona.downloads) || 0,
    }
  },
  watch: {
    persona(nv) {
      this.localDownloads = (nv && nv.downloads) || 0
    },
    visible(v) {
      if (v) {
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', this.handleEsc)
      } else {
        document.body.style.overflow = ''
        window.removeEventListener('keydown', this.handleEsc)
      }
    },
  },
  methods: {
    handleEsc(e) {
      if (e.key === 'Escape') this.$emit('close')
    },
    async handleImport() {
      if (!this.isLoggedIn) return

      const exportData = {
        name: this.persona.name,
        description: this.persona.description,
        prompt: this.persona.prompt || '',
        bg_image: this.persona.bg_image || '',
      }

      // Desktop app (QWebEngineView) → personaimport: URL
      const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(exportData))))
      window.location.href = 'personaimport:' + b64

      // Record download
      fetch(`${API_BASE}/api/personas/${this.persona.id}/download`, { method: 'POST' })
      this.localDownloads += 1
      this.$emit('downloaded', this.persona.id)
    },
  },
  beforeUnmount() {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', this.handleEsc)
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
  padding: 24px;
}

.modal-container {
  background: var(--base);
  border-radius: 18px;
  border: 1px solid var(--surface2);
  max-width: 640px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 28px 28px 20px;
  border-bottom: 1px solid var(--surface1);
}

.modal-icon {
  font-size: 40px;
  line-height: 1;
}

.modal-header-text {
  flex: 1;
}

.modal-title {
  font-size: 22px;
  font-weight: 800;
  color: var(--text);
  margin-bottom: 8px;
}

.modal-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.modal-category {
  font-size: 12px;
  color: var(--lavender);
  background: rgba(144, 160, 208, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
}

.modal-type-persona {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 600;
  color: var(--mauve);
  background: rgba(203, 166, 247, 0.12);
}

.modal-stat {
  font-size: 12px;
  color: var(--overlay1);
}

.modal-author {
  font-size: 12px;
  color: var(--overlay1);
  background: rgba(127, 132, 156, 0.1);
  padding: 3px 10px;
  border-radius: 6px;
}

.modal-close {
  background: none;
  border: none;
  color: var(--overlay0);
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all var(--transition);
}

.modal-close:hover {
  background: var(--surface1);
  color: var(--text);
}

.modal-body {
  flex: 1;
  padding: 24px 28px;
  overflow-y: auto;
}

.modal-desc {
  font-size: 14px;
  color: var(--subtext1);
  line-height: 1.7;
  margin-bottom: 24px;
}

.modal-section {
  margin-bottom: 24px;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--mauve);
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.persona-prompt-block {
  background: var(--mantle);
  border: 1px solid var(--surface1);
  border-radius: 10px;
  padding: 16px 20px;
  border-left: 3px solid var(--mauve);
}

.persona-prompt-text {
  font-size: 13px;
  color: var(--subtext0);
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Segoe UI', sans-serif;
  max-height: 300px;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px 28px;
  border-top: 1px solid var(--surface1);
  display: flex;
  justify-content: flex-end;
}

.btn-import {
  background: linear-gradient(135deg, var(--mauve), var(--pink));
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all var(--transition);
}

.btn-import:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(203, 166, 247, 0.3);
}

.btn-import.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Transitions */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-active .modal-container, .modal-leave-active .modal-container {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal-container {
  transform: scale(0.95) translateY(20px);
}
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
  opacity: 0;
}
</style>
